import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Compass, Footprints, Leaf, Mountain } from 'lucide-react';
import { Badge } from '@/components/shared/Badge';
import { Button } from '@/components/ui/button';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { fadeUp, inViewOnce, staggerContainer } from '@/lib/motion/variants';

const PILLARS = [
  { icon: Footprints, title: 'Earn the view',     body: 'Effort proportional to wonder.', color: brand.teal },
  { icon: Compass,    title: 'Local in the lead', body: 'Guides who grew up here.',        color: brand.chinar },
  { icon: Leaf,       title: 'Leave it better',   body: '8% of every booking → cleanup.',  color: brand.saffron },
];

export function AdventureManifesto() {
  return (
    <section className="mx-auto mt-24 max-w-7xl px-4 md:px-6">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.3fr] lg:items-stretch">
        {/* Left — editorial copy block */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={inViewOnce}
          variants={staggerContainer}
          className="flex flex-col justify-center"
        >
          <motion.div variants={fadeUp}>
            <Badge color={brand.teal} icon={Mountain}>
              The reason we exist
            </Badge>
          </motion.div>

          <motion.h2
            variants={fadeUp}
            className="font-display mt-4 text-4xl font-semibold leading-[1.02] tracking-tight md:text-5xl lg:text-[56px]"
          >
            We are an{' '}
            <em
              style={{
                background: `linear-gradient(135deg, #8BD8C2, ${brand.teal} 40%, ${brand.saffron})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              adventure company
            </em>{' '}
            first.
          </motion.h2>

          <motion.p
            variants={fadeUp}
            className="mt-4 max-w-xl text-[15px] leading-relaxed text-ink-dim md:text-base"
          >
            The hotels and houseboats are real. The vehicles run. But this entire platform exists
            because we believe Kashmir is one of the last places on earth that still humbles people
            who&apos;ve seen everything. <strong className="text-ink">Adventure isn&apos;t a
            tab on this site. It&apos;s the whole point.</strong>
          </motion.p>

          {/* Three pillars */}
          <motion.div
            variants={staggerContainer}
            className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3"
          >
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  variants={fadeUp}
                  className="rounded-2xl border border-[var(--glass-edge)] glass p-4"
                  style={{
                    background: `linear-gradient(180deg, ${p.color}1a, transparent 70%), var(--glass)`,
                  }}
                >
                  <Icon size={18} color={p.color} />
                  <div className="font-display mt-2 text-base font-semibold tracking-tight">
                    {p.title}
                  </div>
                  <div className="mt-1 text-[12px] text-ink-dim">{p.body}</div>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div variants={fadeUp} className="mt-7 flex flex-wrap gap-2.5">
            <Button asChild size="lg">
              <Link to="/adventure">
                Read the oath <ArrowRight size={15} />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/search?type=trek">Browse routes</Link>
            </Button>
          </motion.div>
        </motion.div>

        {/* Right — tall cinematic image */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={inViewOnce}
          variants={fadeUp}
          className="relative overflow-hidden rounded-[24px]"
        >
          <div className="relative aspect-[4/5] md:aspect-[5/6] lg:aspect-auto lg:h-full">
            <img
              src={unsplash(photo.winterTrek, { w: 1400 })}
              alt="A trekker on a snow ridge at dawn"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.9))',
              }}
            />

            {/* Top-left badge */}
            <div className="absolute left-5 top-5 flex flex-wrap gap-2">
              <Badge color={brand.saffron}>Signature route</Badge>
            </div>

            {/* Bottom-left feature */}
            <div className="absolute inset-x-5 bottom-5 text-white">
              <div
                className="text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{ color: '#8BD8C2' }}
              >
                01 / Tarsar Marsar
              </div>
              <h3
                className="font-display mt-2 text-3xl font-semibold leading-tight tracking-tight md:text-4xl"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
              >
                Two glacial lakes,<br />
                one 4,000m pass.
              </h3>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-white/85">
                <span className="rounded-full border border-white/25 bg-white/10 px-2.5 py-1 backdrop-blur-md">
                  6 days
                </span>
                <span className="rounded-full border border-white/25 bg-white/10 px-2.5 py-1 backdrop-blur-md">
                  Moderate
                </span>
                <span className="rounded-full border border-white/25 bg-white/10 px-2.5 py-1 backdrop-blur-md">
                  13,123 ft max
                </span>
                <span className="rounded-full border border-white/25 bg-white/10 px-2.5 py-1 backdrop-blur-md">
                  May – Oct
                </span>
              </div>
              <Link
                to="/adventure"
                className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold"
                style={{ color: '#8BD8C2' }}
              >
                See all signature routes <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
